import React, { useState } from 'react'
import { useQuery } from 'react-query'
import { getBlogPosts, BlogPostListItem, BlogPostsResponse } from '../../../moduls/blog'
import { getReadableDate } from '../../../services/utils'

const getPageItems = (current: number, last: number): Array<number | 'ellipsis'> => {
  if (last <= 7) {
    return Array.from({ length: last }, (_, i) => i + 1)
  }
  const items: Array<number | 'ellipsis'> = [1]
  const start = Math.max(2, current - 1)
  const end = Math.min(last - 1, current + 1)
  if (start > 2) items.push('ellipsis')
  for (let i = start; i <= end; i++) items.push(i)
  if (end < last - 1) items.push('ellipsis')
  items.push(last)
  return items
}

const Pagination: React.FC<{ current: number; last: number; onChange: (page: number) => void }> = ({
  current,
  last,
  onChange,
}) => {
  if (last <= 1) return null
  const items = getPageItems(current, last)
  const baseBtn =
    'min-w-[40px] h-10 px-3 flex items-center justify-center rounded-lg border text-[16px] transition-colors'
  return (
    <nav
      aria-label="Stránkování blogu"
      className="col-span-12 flex flex-wrap items-center justify-center gap-2 mt-6"
    >
      <button
        type="button"
        onClick={() => onChange(current - 1)}
        disabled={current === 1}
        className={`${baseBtn} border-gray-200 text-[#0183BA] hover:bg-[#0183BA] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#0183BA]`}
        aria-label="Předchozí stránka"
      >
        ‹
      </button>
      {items.map((item, idx) =>
        item === 'ellipsis' ? (
          <span key={`e-${idx}`} className="px-2 text-gray-400 select-none">
            …
          </span>
        ) : (
          <button
            key={item}
            type="button"
            onClick={() => onChange(item)}
            aria-current={item === current ? 'page' : undefined}
            className={`${baseBtn} ${
              item === current
                ? 'border-[#0183BA] bg-[#0183BA] text-white'
                : 'border-gray-200 text-[#0183BA] hover:bg-[#0183BA] hover:text-white'
            }`}
          >
            {item}
          </button>
        )
      )}
      <button
        type="button"
        onClick={() => onChange(current + 1)}
        disabled={current === last}
        className={`${baseBtn} border-gray-200 text-[#0183BA] hover:bg-[#0183BA] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#0183BA]`}
        aria-label="Další stránka"
      >
        ›
      </button>
    </nav>
  )
}

const FeaturedCard: React.FC<{ post: BlogPostListItem }> = ({ post }) => (
  <a
    href={`/blog/${post.slug}`}
    className="col-span-12 flex flex-col lg:grid lg:grid-cols-12 gap-3 overflow-hidden block mb-6 group"
  >
    {post.cover_image && (
      <div className="lg:col-span-5">
        <img
          src={post.cover_image}
          alt={post.title}
          className="w-full aspect-[1600/1131] object-cover border border-gray-200 rounded-xl"
        />
      </div>
    )}
    <div
      className={`${post.cover_image ? 'lg:col-span-7' : 'lg:col-span-12'} flex flex-col lg:justify-center lg:p-6`}
    >
      {post.published_at && (
        <div className="flex items-center gap-2 text-[#A8CD00] text-[16px] mb-2">
          <span className="shrink-0">{getReadableDate(post.published_at)}</span>
          <span className="flex-1 h-px bg-[#A8CD00]" />
        </div>
      )}
      <h2 className="text-[#0183BA] mb-3 text-xl md:text-[25px] group-hover:text-[#015f87] transition-colors">
        {post.title}
      </h2>
      {post.excerpt && <p className="text-[#333333] text-[16px]">{post.excerpt}</p>}
    </div>
  </a>
)

const GridCard: React.FC<{ post: BlogPostListItem }> = ({ post }) => (
  <a
    href={`/blog/${post.slug}`}
    className="col-span-12 lg:col-span-4 flex flex-col overflow-hidden block mb-6 group"
  >
    {post.cover_image && (
      <img
        src={post.cover_image}
        alt={post.title}
        className="w-full aspect-[1600/1131] object-cover border border-gray-200 rounded-xl mb-3"
      />
    )}
    <div className="flex flex-col flex-1">
      {post.published_at && (
        <div className="flex items-center gap-2 text-[#A8CD00] text-[16px] mb-2">
          <span className="shrink-0">{getReadableDate(post.published_at)}</span>
          <span className="flex-1 h-px w-full bg-[#A8CD00]" />
        </div>
      )}
      <h2 className="text-[#0183BA] mb-3 text-xl md:text-[25px] group-hover:text-[#015f87] transition-colors">
        {post.title}
      </h2>
      {post.excerpt && <p className="text-[#333333] text-[16px]">{post.excerpt}</p>}
    </div>
  </a>
)

const Blog: React.FC = () => {
  const [page, setPage] = useState(1)
  const { data, isLoading, isError } = useQuery<BlogPostsResponse>(
    ['blog-posts', page],
    () => getBlogPosts(page),
    { keepPreviousData: true }
  )

  const isFirstPage = page === 1
  const featured = data?.featured ?? null
  const pinned = data?.pinned ?? []
  const regular = data?.data ?? []
  // First-page fallback: when admin hasn't picked a featured article, promote
  // the first pinned, otherwise the newest regular one. Keeps the layout from
  // collapsing into a flat grid. Only applies on page 1 — deeper pages render
  // a flat grid of regular posts.
  const fallbackFeatured = isFirstPage
    ? !featured && pinned.length > 0
      ? pinned[0]
      : !featured && regular.length > 0
      ? regular[0]
      : null
    : null
  const heroPost = isFirstPage ? featured ?? fallbackFeatured : null
  const pinnedAfterHero = featured ? pinned : pinned.filter((p) => p.id !== heroPost?.id)
  const regularAfterHero =
    isFirstPage && !featured ? regular.filter((p) => p.id !== heroPost?.id) : regular
  const hasAny = !!heroPost || pinnedAfterHero.length > 0 || regularAfterHero.length > 0
  const lastPage = data?.meta?.last_page ?? 1

  const handlePageChange = (next: number) => {
    if (next < 1 || next > lastPage || next === page) return
    setPage(next)
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  return (
    <div className="px-4 lg:px-0">
      <div className="container mx-auto mt-[52px] md:mt-[88px] py-12">
        <h1 className="text-[#0183BA] text-xl md:text-[25px] mb-8 text-center">Blog</h1>
        {isLoading && <p className="text-center text-gray-400">Načítám příspěvky…</p>}
        {isError && <p className="text-center text-red-400">Nepodařilo se načíst příspěvky.</p>}
        {!isLoading && !isError && !hasAny && (
          <p className="text-center text-gray-400">Zatím nejsou k dispozici žádné články.</p>
        )}
        {hasAny && (
          <div className="grid grid-cols-12 gap-4">
            {heroPost && <FeaturedCard post={heroPost} />}
            {pinnedAfterHero.map((post) => (
              <GridCard key={post.slug} post={post} />
            ))}
            {regularAfterHero.map((post) => (
              <GridCard key={post.slug} post={post} />
            ))}
            <Pagination current={page} last={lastPage} onChange={handlePageChange} />
          </div>
        )}
      </div>
    </div>
  )
}

export default Blog
